type ExternalScriptLoaderOptions = {
  errorMessage?: string;
  cacheResolved?: boolean;
  removeOnError?: boolean;
};

const scriptLoads = new Map<string, Promise<void>>();
const ownedScripts = new Map<string, HTMLScriptElement>();

const hasExistingScript = (src: string): boolean => {
  try {
    const resolvedSrc = new URL(src, document.baseURI).href;
    return Array.from(document.scripts).some((script) => script.src === resolvedSrc);
  } catch {
    return false;
  }
};

/**
 * Loads an external script once per URL and shares an in-flight request between callers.
 * Failed requests are removed from the cache so a later call can retry.
 */
export const loadExternalScript = (
  src: string,
  {
    errorMessage = `Failed to load external script: ${src}`,
    cacheResolved = true,
    removeOnError = true,
  }: ExternalScriptLoaderOptions = {}
): Promise<void> => {
  const cachedLoad = scriptLoads.get(src);
  if (cachedLoad) {
    return cachedLoad;
  }

  if (cacheResolved && hasExistingScript(src)) {
    const existingScriptLoad = Promise.resolve();
    scriptLoads.set(src, existingScriptLoad);
    return existingScriptLoad;
  }

  const scriptRequest = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(errorMessage));
    document.body.appendChild(script);
    ownedScripts.set(src, script);
  });
  scriptLoads.set(src, scriptRequest);

  scriptRequest.then(
    () => {
      if (!cacheResolved && scriptLoads.get(src) === scriptRequest) {
        scriptLoads.delete(src);
      }
    },
    () => {
      if (scriptLoads.get(src) === scriptRequest) {
        scriptLoads.delete(src);
        if (removeOnError) {
          ownedScripts.get(src)?.remove();
          ownedScripts.delete(src);
        }
      }
    }
  );

  return scriptRequest;
};

export const resetExternalScript = (src: string, removeScript = true): void => {
  scriptLoads.delete(src);
  if (removeScript) {
    ownedScripts.get(src)?.remove();
    ownedScripts.delete(src);
  }
};
